import { useTranslation } from 'react-i18next';
import { boundingBoxOf, type DetectedHold } from '../../../types/climb-scan';
import { isRectangle, outlineClipPath, type OutlinePoint } from '../../../components/climbs/hold-outline';
import { BOX_GRID } from './detected-holds';

export interface ScanColorPreview {
  colorId: string;
  /** What the analysis called the colour, for the circle's label. */
  label: string;
  /** The hold's own colour, which is what the circles are drawn in. */
  hex: string | null;
  /** The holds the analysis found, as shapes on its 0-1000 grid. */
  holds: DetectedHold[];
}

interface ScanColorPreviewsProps {
  previews: ScanColorPreview[];
  onSelect: (colorId: string) => void;
}

/**
 * The climbs on this wall that are still to be done, drawn on the photo in
 * their own hold colour and pressable.
 *
 * It answers "what else is on this wall, and where" without leaving the climb
 * being worked on — and makes the photo a way into the list rather than only a
 * view of it: a setter looking at the wall can press the yellow holds instead
 * of working out which row "Giallo" is.
 *
 * Drawn dashed and faint on purpose. These are somebody else's climb: they have
 * to be visible enough to press and quiet enough not to be mistaken for the
 * marks being edited.
 *
 * A hold that knows its own shape is **clipped to it**, which the browser also
 * honours when hit-testing, so the press lands on the hold rather than on the
 * wall beside it. A detection that is only a rectangle says nothing about shape
 * and stays the ring it has always been.
 */
export function ScanColorPreviews({ previews, onSelect }: ScanColorPreviewsProps) {
  const { t } = useTranslation();

  return (
    <>
      {previews.map((preview) =>
        preview.holds.map((hold, index) => {
          const box = boundingBoxOf(hold.outline);
          const shaped = !isRectangle(hold.outline);

          return (
            <button
              key={`${preview.colorId}-${index}`}
              type="button"
              aria-label={t('gymAdmin.scans.openClimbOnPhoto', { name: preview.label })}
              title={preview.label}
              data-testid="scan-color-preview"
              data-color-id={preview.colorId}
              // The frame beneath reads a press as "draw a circle here", so this
              // one has to stop before it gets there.
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onSelect(preview.colorId);
              }}
              className={`absolute border-2 border-dashed opacity-70 transition-opacity hover:opacity-100 ${
                shaped ? '' : 'rounded-full'
              }`}
              style={{
                left: `${(box.x / BOX_GRID) * 100}%`,
                top: `${(box.y / BOX_GRID) * 100}%`,
                width: `${(box.width / BOX_GRID) * 100}%`,
                height: `${(box.height / BOX_GRID) * 100}%`,
                borderColor: preview.hex ?? 'rgba(255,255,255,0.9)',
                clipPath: shaped ? outlineClipPath(hold.outline as OutlinePoint[]) : undefined,
              }}
            />
          );
        })
      )}
    </>
  );
}
